import { defineStore } from 'pinia';
import { useMapStoreData, useCategoriesStore, useSearchData, useEventStore, useFavoritesStore } from '@/stores/index';
import mapboxgl from 'mapbox-gl';

export const useSetMap = defineStore('setMap', {
	state: () => ({
		map: null as any,
		layoutName: null as any,
		favoritedPathName: null as any,
		markerPathName: null as any,
		modulePathName: null as any,
		allMapData: null as any,
		mapLoader: true,
	}),
	actions: {
		// since we rely on `this`, we cannot use an arrow function
		setMapBox(mapbox: any) {
			this.map = mapbox;
		},
		fullMapReset() {
			useMapStoreData().setActivePoi(null);
			useMapStoreData().showMap = false;
			this.centerMapbox();
			this.deletePath('markerPath');
			this.deletePath('favoritedPath');
			this.deletePath('modulePath');
		},
		createPath(name: any, pathsource: any, type: string) {
			this.deletePath(type);
			if (type == 'markerPath') {
				this.markerPathName = name;
			} else if (type == 'favoritedPath') {
				this.favoritedPathName = name;
			} else if (type == 'modulePath') {
				this.modulePathName = name;
			}
			this.layoutName = name;
			const color = getComputedStyle(document.documentElement).getPropertyValue('--nature-color-start').trim();
			this.map.addLayer({
				id: this.layoutName,
				type: 'line',
				source: {
					type: 'geojson',
					data: pathsource,
				},
				layout: {
					'line-join': 'round',
					'line-cap': 'round',
				},
				paint: {
					'line-width': ['interpolate', ['linear'], ['zoom'], 0, 2, 10, 4, 22, 10],
					//'line-blur': 10.5,
					'line-color': color,
				},
			});
		},
		deletePath(type: string = 'markerPath') {
			const layout =
				type == 'markerPath' ? this.markerPathName : type == 'favoritedPath' ? this.favoritedPathName : this.modulePathName;
			if (layout != null && this.map.getLayer(layout) != undefined) {
				this.map.removeLayer(layout);
				this.map.removeSource(layout);
			}
			if (type == 'markerPath') this.markerPathName = null;
			else if (type == 'favoritedPath') this.favoritedPathName = null;
			else if (type == 'modulePath') this.modulePathName = null;
		},
		setMapData(data: any) {
			this.map.getSource('clustering').setData(data);
		},
		setAllMarkersData() {
			// When map is opened from a mapCopy module, keep showing only those POIs (don’t overwrite with full list)
			const mapStore = useMapStoreData();
			if (mapStore.mapPoiFilter != null && mapStore.mapPoiFilter.length > 0) {
				this.returnFilteredMarkersMap();
				return;
			}
			if (this.map != null && this.map.getSource('clustering') != undefined) {
				const validEvents = useEventStore().validEventsOnMap.filter(
					(value) => !useEventStore().invalidEventsIds.includes(value)
				);
				useEventStore().eventNumberFilter = validEvents.length;
				if ((useMapStoreData().clusters as any).features.length > 0) {
					let resultEvents = (useMapStoreData().clusters as any).features.filter((cluster: any) => {
						return !useEventStore().invalidEventsIds.includes(cluster.properties.id);
					});
					if (useCategoriesStore().defaultActiveFiltersParentsId.length > 0) {
						resultEvents = (resultEvents as any).filter((cluster: any) => {
							return useCategoriesStore().defaultActiveFiltersParentsId.includes(cluster.properties.categories[0]);
						});
					}
					const map1 = {
						type: 'FeatureCollection',
						features: resultEvents,
					};
					this.map.getSource('clustering').setData(map1);
				}
			}
		},
		searchMapFilter() {
			if ((useMapStoreData().clusters as any).features.length > 0) {
				const result = (useMapStoreData().clusters as any).features.filter((item: any) =>
					useSearchData().searchFilter(item.id, item.properties.categories)
				);

				const source = this.map.getSource('closeSourceMarker')._data;
				const containsSourceId = result.some((item: any) => item.id === source.id);

				//if poi is opened and is not in the search result, close marker from the map
				if (!containsSourceId) {
					useMapStoreData().setActivePoi(null);
				}

				const map1 = {
					type: 'FeatureCollection',
					features: result,
				};
				this.map.getSource('clustering').setData(map1);

				const sourceCluster = this.map.getSource('clustering');

				this.moveToFilterBounds(sourceCluster);
			}
			//this.map.getSource('clustering').setData(useMapStoreData().clusters);
		},
		returnFilteredMarkersMap() {
			if (this.map == null || this.map.getSource('clustering') == undefined) return;
			const mapPoiFilter = useMapStoreData().mapPoiFilter;
			let result: any = '';
			let resultEventsWith: any = '';
			let numberOfEvents = 0;

			// Debug: log when MapCopy filter is applied (pois or tag→pois)
			if (mapPoiFilter != null && mapPoiFilter.length > 0) {
				console.log('[mapPoiFilter] returnFilteredMarkersMap called WITH POI filter:', mapPoiFilter);
			}

			if (mapPoiFilter != null && mapPoiFilter.length > 0) {
				// MapCopy mode: show only module.pois – category/POI filters are NOT applied; listed POIs are always visible
				const clusters = (useMapStoreData().clusters as any).features;
				const poiIdSet = new Set(mapPoiFilter.map((id: any) => String(id)));
				// Log: POIs we want to DISPLAY (from module.pois)
				console.log('[mapPoiFilter] POIs to display (module.pois):', [...poiIdSet]);
				// Keep only features whose id is IN poiIdSet (show these, hide everything else)
				resultEventsWith = clusters.filter((cluster: any) => {
					const id = cluster.properties?.id ?? cluster.id;
					const idStr = id != null ? String(id) : '';
					const included = poiIdSet.has(idStr);
					return included && !useEventStore().invalidEventsIds.includes(cluster.properties?.id);
				});
				// Log: which POI ids we actually kept (for debugging id format)
				const keptIds = resultEventsWith.map((f: any) => String(f.properties?.id ?? f.id ?? ''));
				console.log('[mapPoiFilter] Features kept (displayed on map):', keptIds.length, keptIds);
				if (resultEventsWith.length > 0 && resultEventsWith.length < clusters.length) {
					console.log('[mapPoiFilter] Sample feature id format:', {
						'cluster.properties?.id': resultEventsWith[0].properties?.id,
						'cluster.id': resultEventsWith[0].id,
					});
				}
				if (keptIds.length === 0) {
					console.warn('[mapPoiFilter] No features matched – check that pois/tag ids match. First 3 ids in data:', clusters.slice(0, 3).map((f: any) => f.properties?.id ?? f.id));
				}
			} else {
				// Normal mode: filter by category and events
				console.log('[mapPoiFilter] returnFilteredMarkersMap – no POI filter, using category/events');
				if (useSearchData().searchFilteredItems.length > 0) {
					result = useSearchData().searchFilteredItems.filter((item: any) =>
						useCategoriesStore().filterMarkersByCategory(item.properties.categories)
					);
				} else {
					result = (useMapStoreData().clusters as any).features.filter((item: any) =>
						useCategoriesStore().filterMarkersByCategory(item.properties.categories)
					);
				}

				if (!useEventStore().showEvents) {
					const allEvent = useEventStore().validEventsOnMap;
					result = result.filter((item: any) => !allEvent.includes(item.id));
				}

				resultEventsWith = result.filter((cluster: any) => {
					return !useEventStore().invalidEventsIds.includes(cluster.properties.id);
				});
			}

			resultEventsWith.forEach((item: any) => {
				if (item.properties.type == 'event') {
					numberOfEvents++;
				}
			});

			useEventStore().eventNumberFilter = numberOfEvents;

			const map1 = {
				type: 'FeatureCollection',
				features: resultEventsWith,
			};
			this.map.getSource('clustering').setData(map1);

			// MapCopy mode: only show favorites that are in module.pois (or tag→pois) so favorited POIs from regular map don't appear
			const favSource = this.map.getSource('favoritesSource');
			if (favSource) {
				if (mapPoiFilter != null && mapPoiFilter.length > 0) {
					const displayedIdSet = new Set(resultEventsWith.map((f: any) => String(f.properties?.id ?? f.id ?? '')));
					const favoritesGeoJson = useFavoritesStore().favoritesGeoJson ?? [];
					const filteredFavorites = favoritesGeoJson.filter((f: any) =>
						displayedIdSet.has(String(f.properties?.id ?? ''))
					);
					favSource.setData({
						type: 'FeatureCollection',
						features: filteredFavorites,
					});
				} else {
					// Normal mode: restore full favorites layer
					favSource.setData({
						type: 'FeatureCollection',
						features: useFavoritesStore().favoritesGeoJson ?? [],
					});
				}
			}
		},
		moveToFilterBounds(sourceCluster: any) {
			const width = window.innerWidth;
			let padding = 250;
			if (width < 800) {
				padding = 30;
			}
			if (sourceCluster.type === 'geojson') {
				const data = sourceCluster._data;

				if (data.type === 'FeatureCollection') {
					const markers = data.features;
					if (markers.length == 0) {
						//this.centerMapbox();
						return;
					}
					const bounds = markers.reduce(
						function (bounds: any, marker: any) {
							return bounds.extend(marker.geometry.coordinates);
						},
						new mapboxgl.LngLatBounds(markers[0].geometry.coordinates, markers[0].geometry.coordinates)
					);

					this.map.fitBounds(bounds, {
						padding: padding,
						maxZoom: 14,
					});
				}
			} else if (Array.isArray(sourceCluster)) {
				const bounds = sourceCluster.reduce(
					function (bounds, marker) {
						return bounds.extend(marker.geometry.coordinates);
					},
					new mapboxgl.LngLatBounds(sourceCluster[0].geometry.coordinates, sourceCluster[0].geometry.coordinates)
				);

				// Adjust the map view to fit the bounding box
				this.map.fitBounds(bounds, {
					padding: padding, // padding around the box for better view
					maxZoom: 18, // this can be set to ensure the map doesn't zoom in too far
				});
			}
		},
	},
	getters: {
		centerMapbox: (state) => () => {
			const options: any = useMapStoreData().options;

			if (options != null) {
				if (useMapStoreData().isOnline) {
					state.map.flyTo({
						center: [options['lng'], options['lat']],
						zoom: options.zoom,
						essential: true, // this animation is considered essential with respect to prefers-reduced-motion
					});
				}
			}
		},
	},
});
